import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { isMacOS } from '../../../../utils/browserDetect'
import type { TeamMember } from '../../constants'
import { MemberInfo } from './MemberInfo'

interface MemberProps extends TeamMember {
  scene: string
  isHoverDisabled: boolean
  enableHover: () => void
  onVideoLoaded: () => void
}

const Member: React.FC<MemberProps> = memo(
  ({
    name,
    role,
    idle,
    idleWebm,
    voice,
    voiceWebm,
    zIndex,
    top,
    scene,
    isHoverDisabled,
    transform,
    enableHover,
    onVideoLoaded,
  }) => {
    const [hovered, setHovered] = useState(false)
    const voiceVideoRefs = useRef<HTMLVideoElement | null>(null)
    const idleVideoRefs = useRef<HTMLVideoElement | null>(null)
    const timeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const useWebm = !isMacOS()

    const handleMouseEnter = useCallback(
      (firstTime = false) => {
        if ((scene !== 'start' || isHoverDisabled) && !firstTime) return
        if (timeOutRef.current) {
          clearTimeout(timeOutRef.current)
        }

        timeOutRef.current = setTimeout(() => {
          setHovered(true)
          const idleVideo = idleVideoRefs.current
          const voiceVideo = voiceVideoRefs.current
          if (idleVideo) {
            idleVideo.pause()
          }
          if (voiceVideo) {
            voiceVideo.currentTime = 0
            void voiceVideo.play()
          }
        }, 500)
      },
      [scene, isHoverDisabled],
    )

    const handleMouseLeave = useCallback(() => {
      if (isHoverDisabled) return
      if (timeOutRef.current) {
        clearTimeout(timeOutRef.current)
      }
      setHovered(false)
      const idleVideo = idleVideoRefs.current
      const voiceVideo = voiceVideoRefs.current
      if (voiceVideo) {
        voiceVideo.pause()
        voiceVideo.currentTime = 0
      }
      if (idleVideo) {
        void idleVideo.play()
      }
    }, [isHoverDisabled])

    const handleVoiceVideoEnded = useCallback(() => {
      if (name === 'Kai' && isHoverDisabled) {
        enableHover()
      }
      handleMouseLeave()
    }, [name, isHoverDisabled, enableHover, handleMouseLeave])

    useEffect(() => {
      if (scene === 'start' && name === 'Kai' && isHoverDisabled) {
        handleMouseEnter(true)
      }
      if (!isHoverDisabled) {
        handleMouseLeave()
      }
    }, [scene, name, isHoverDisabled, handleMouseEnter])

    useEffect(() => {
      if (voiceVideoRefs.current) {
        voiceVideoRefs.current.volume = 0.2
      }
    }, [voiceVideoRefs.current])

    return (
      <div
        className={`group flex-1 -mx-[2.5%] relative w-[11%]
        ${scene === 'start' ? 'cursor-pointer' : ''} 
        ${transform ? transform : ''} ${zIndex} transition-opacity 
        ${!scene || scene === 'start' ? 'opacity-100' : 'opacity-0'}`}
        onMouseEnter={() => handleMouseEnter()}
        onMouseLeave={handleMouseLeave}
      >
        {scene === 'start' && !isHoverDisabled && (
          <MemberInfo name={name} role={role} className="opacity-0" />
        )}

        <video
          ref={idleVideoRefs}
          autoPlay
          loop
          muted
          playsInline
          disablePictureInPicture
          preload="auto"
          onLoadedData={onVideoLoaded}
          className={`absolute max-w-none h-[130%] ${hovered ? 'opacity-0' : 'opacity-100'} ${top ? top : 'top-[20px]'} left-1/2 transform -translate-x-1/2 w-auto object-cover object-center pointer-events-none`}
        >
          {useWebm ? (
            <source src={idleWebm} type="video/webm" />
          ) : (
            <source src={idle} type='video/mp4; codecs="hvc1"' />
          )}
        </video>

        {(scene === 'start' || name === 'Kai') && (
          <video
            ref={voiceVideoRefs}
            playsInline
            disablePictureInPicture
            preload="auto"
            className={`absolute max-w-none h-[130%] ${hovered ? 'opacity-100' : 'opacity-0'} ${top ? top : 'top-[20px]'} left-1/2 transform -translate-x-1/2 w-auto object-cover object-center pointer-events-none`}
            onEnded={handleVoiceVideoEnded}
          >
            {useWebm ? (
              <source src={voiceWebm} type="video/webm" />
            ) : (
              <source src={voice} type='video/mp4; codecs="hvc1"' />
            )}
          </video>
        )}
      </div>
    )
  },
)

export { Member }
